import type { ComponentProps } from 'react';
import React from 'react';

import { gql } from '@soundxyz/gql-string';
import type { CSS } from '@components-shared/src/stitches';
import { useLikeRelease } from '@components/Likes/useLikeRelease';
import { AddAsFeatureMenuItem } from '@components/basics/Dropdowns/MenuItems/AddAsFeatureMenuItem';
import { AddToQueueMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToQueueMenuItem';
import { AddToShelfMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToShelfMenuItem';
import { LikeSoundMenuItem } from '@components/basics/Dropdowns/MenuItems/LikeSoundMenuItem';
import { ShareMenuItem } from '@components/basics/Dropdowns/MenuItems/ShareMenuItem';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import type { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { MediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, SongCardDropdownInfoFragmentDoc } from '@graphql/reactQuery';
import { trackClickEvent } from '@lib/analytics';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment SongCardDropdownInfo on Release {
    id
    type
    track {
      id
    }
    ...LikeSoundMenuItem
  }
`);

/**
 * This is an internal component used by song card dropdown
 * The reason we export it is so we can test it without having to deal with the dropdown menu
 */
export const SongCardDropdownItems = ({
  release,
  isNonArtistOwner,
  showLikeSound,
}: {
  release: FragmentType<SongCardDropdownInfoFragmentDoc>;
  isNonArtistOwner: boolean;
  showLikeSound: boolean;
}) => {
  const releaseInfo = getFragment(SongCardDropdownInfoFragmentDoc, release);

  const { track, id, type } = releaseInfo;

  const isAlbum = type === 'ALBUM';

  const hasAudioAccess = releaseInfo.type !== 'ALBUM';

  const { isLiked, onClick: onLikeClick } = useLikeRelease({
    id: releaseInfo.id,
    source: 'Card',
  });

  return (
    <>
      {showLikeSound && !isAlbum && (
        <LikeSoundMenuItem liked={isLiked} onClick={onLikeClick} data={releaseInfo} />
      )}
      {!isAlbum && <AddToShelfMenuItem releaseId={id} disabled={!hasAudioAccess} source="Card" />}
      {!isAlbum && (
        <AddToQueueMenuItem
          trackId={track.id}
          disabled={!track.id || !hasAudioAccess}
          emitAnalytics={() => {
            trackClickEvent({
              event: 'Add to Queue Clicked',
              properties: {
                associationId: releaseInfo.id,
                associationType: 'Release',
                sourceComponent: 'Card',
              },
            });
          }}
        />
      )}
      {isNonArtistOwner && <AddAsFeatureMenuItem releaseId={id} />}
      <ShareMenuItem releaseId={releaseInfo.id} source="Song Card" />
    </>
  );
};

export const SongCardDropdown = ({
  release,
  isDropdownOpen,
  setIsDropdownOpen,
  isNonArtistOwner = false,
  triggerStyles,
  showLikeSound = true,
  variant,
}: {
  release: FragmentType<SongCardDropdownInfoFragmentDoc>;
  isDropdownOpen: boolean;
  setIsDropdownOpen: (isDropdownOpen: boolean) => void;
  isNonArtistOwner?: boolean;
  triggerStyles?: CSS;
  /**
   * TODO: remove this once we swap all the song card.
   * https://linear.app/sound/issue/ENG-2833/remove-like-sound-item-from-song-card-dropdown
   */
  showLikeSound?: boolean;
  variant?: ComponentProps<typeof IconButton>['variant'];
}) => {
  const { tablet } = MediaQuery.useContainer();

  return (
    <ThreeDotsDropdown
      isDropdownOpen={isDropdownOpen}
      setIsDropdownOpen={setIsDropdownOpen}
      dropdownProps={{
        css: {
          display: 'flex',
        },
        align: tablet ? 'start' : 'end',
      }}
      triggerProps={{
        css: {
          display: 'flex',
          alignItems: 'center',
          width: 32,
          height: 32,
          ...triggerStyles,
        },
      }}
      variant={variant}
    >
      <SongCardDropdownItems
        release={release}
        isNonArtistOwner={isNonArtistOwner}
        showLikeSound={showLikeSound}
      />
    </ThreeDotsDropdown>
  );
};
